import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  FileText, 
  Settings, 
  User,
  Home,
  Calculator,
  LayoutDashboard,
  History,
  Clock as ClockIcon,
  Building2,
  UserCheck,
  UserPlus,
  CreditCard,
  LogOut,
  Activity,
  MessageSquare
} from 'lucide-react';
import { EstimationLogo } from '../../assets/EstimationLogo';
import { Button } from '../ui/Button';
import { cn } from '../../utils/cn';
import { useAuth } from '../../hooks/useAuth';
import { ScrollArea } from '../ui/ScrollArea';
import { SidebarLoading } from './SidebarLoading';
import { OrganizationSwitcher } from '../organization/OrganizationSwitcher';
import { FeedbackModal } from '../feedback/FeedbackModal';

interface SidebarProps {
  className?: string;
  showOrgSwitcher?: boolean;
  showLogo?: boolean;
  sidebarNavIncludeIds?: string[];
  sidebarNavRemoveIds?: string[];
}

export function Sidebar({ 
  className, 
  showOrgSwitcher = true, 
  showLogo = true,
  sidebarNavIncludeIds,
  sidebarNavRemoveIds
}: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  
  // TEMPORARY: Force admin access on admin.localhost
  const isLocalAdminSubdomain = window.location.hostname === 'admin.localhost';
  const effectiveUser = isLocalAdminSubdomain ? {
    ...user,
    role: 'superadmin',
    isSuperAdmin: true,
    isAdmin: true
  } : user;

  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const quotesTab = queryParams.get('tab');

  // Billing is disabled for all users - only enterprise unlimited plans now
  const isBillingEnabled = false;

  const navigationItems = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: LayoutDashboard,
      href: '/dashboard',
      current: location.pathname === '/dashboard'
    },
    ...(effectiveUser?.role === 'admin' || effectiveUser?.role === 'superadmin' ? [{
      id: 'company-dashboard',
      title: 'Company Dashboard',
      icon: Building2,
      href: '/company-dashboard',
      current: location.pathname === '/company-dashboard'
    }] : []),
    {
      id: 'calculator',
      title: 'Quote (Classic)',
      icon: Calculator,
      href: '/calculator',
      current: location.pathname === '/calculator'
    },
    {
      id: 'new-quote',
      title: 'Quote (New)',
      icon: Calculator,
      href: '/new-quote',
      current: location.pathname === '/new-quote',
      badge: 'Beta'
    },
    // China Quote for admins and superadmins
    ...(effectiveUser?.role === 'admin' || effectiveUser?.role === 'superadmin' ? [{
      id: 'china-quote',
      title: 'China Quote',
      icon: Calculator,
      href: '/china',
      current: location.pathname === '/china' || location.pathname === '/china/admin',
      badge: 'CN'
    }] : []),
    {
      id: 'quotes',
      title: 'Quotes',
      icon: FileText,
      href: '/quotes?tab=recent',
      current: location.pathname.startsWith('/quotes')
    },
    // System Monitoring for superadmins only
    ...(effectiveUser?.role === 'superadmin' ? [{
      id: 'monitoring',
      title: 'System Monitoring',
      icon: Activity,
      href: '/monitoring',
      current: location.pathname === '/monitoring'
    }] : [])
  ];

  const organizationItems = [
    {
      id: 'org-members',
      title: 'Org Members',
      icon: UserCheck,
      href: '/org/members',
      current: location.pathname === '/org/members'
    },
    {
      id: 'invite-members',
      title: 'Invite Members',
      icon: UserPlus,
      href: '/org/invite',
      current: location.pathname === '/org/invite'
    },
    // Billing is completely disabled - all users have enterprise unlimited
    ...(isBillingEnabled ? [{
      id: 'plans-billing',
      title: 'Plans & Billing',
      icon: CreditCard,
      href: '/org/billing',
      current: location.pathname === '/org/billing'
    }] : []),
    {
      id: 'org-settings',
      title: 'Settings',
      icon: Settings,
      href: '/org/settings',
      current: location.pathname === '/org/settings'
    }
  ];

  const adminItems = [
    {
      id: 'admin',
      title: 'Unified Admin Panel',
      icon: Settings,
      action: 'admin-panel',
      requiresRole: ['superadmin']
    }
  ];

  return (
    <aside className={cn("h-full w-full bg-card border-r border-border", className)}>
      {showLogo && (
        <div className="flex h-16 items-center px-6 border-b border-border">
          <div className="flex items-center gap-3">
            <EstimationLogo className="h-6 w-6" variant="dark" />
            <span className="text-lg font-bold tracking-wider text-foreground transform skew-x-[-1deg]" style={{fontFamily: 'ui-sans-serif, system-ui, sans-serif', fontWeight: '900'}}>
              ESTIMATION<span className="text-accent">.PK</span>
            </span>
          </div>
        </div>
      )}

      <div className="px-6 py-4">
        {/* User Info Display (click to open profile) */}
        <div
          className={cn(
            "mb-4 p-3 rounded-md cursor-pointer transition",
            location.pathname === '/profile' 
              ? "bg-accent/10 border border-accent/20" 
              : "bg-accent/5 hover:bg-accent/10"
          )}
          title="View profile"
          onClick={() => navigate('/profile')}
          role="button"
          aria-label="Open user profile"
        >
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center",
              location.pathname === '/profile' 
                ? "bg-accent/20" 
                : "bg-accent/10"
            )}>
              <span className={cn(
                "text-sm font-medium",
                location.pathname === '/profile' 
                  ? "text-accent" 
                  : "text-accent"
              )}>
                {(user?.name || 'Sahal').charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className={cn(
                "text-sm font-medium truncate",
                location.pathname === '/profile' && "text-accent"
              )}>
                {user?.name || 'Sahal (Super Admin)'}
              </p>
              <p className="text-xs text-muted-foreground capitalize">
                {effectiveUser?.role === 'superadmin' ? 'Super Admin' : effectiveUser?.role || 'Admin'}
              </p>
            </div>
          </div>
        </div>

        {/* Organization Switcher */}
        {showOrgSwitcher && (
          <div className="mb-4">
            <OrganizationSwitcher className="w-full" />
          </div>
        )}
      </div>

      <ScrollArea className="flex-1 px-6" style={{ height: "calc(100vh - 16rem)" }}>
        <div className="pb-6">
          {/* Main Navigation */}
          <div className="space-y-1">
            {navigationItems.map((item) => (
              <Button
                key={item.id}
                variant={item.current ? "outline" : "ghost"}
                size="sm"
                className={cn(
                  "w-full justify-start gap-3 h-10 px-3 font-normal",
                  item.current ? "bg-accent/10 text-accent border-accent/20" : "text-muted-foreground hover:text-foreground hover:bg-accent/10"
                )}
                onClick={() => navigate(item.href)}
              >
                <item.icon className="h-4 w-4" />
                <span className="text-sm">{item.title}</span>
                {item.badge && (
                  <span className="ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded bg-accent/20 text-accent">
                    {item.badge}
                  </span>
                )}
              </Button>
            ))}
          </div>

          {/* Organization Section */}
          {(effectiveUser?.role === 'admin' || effectiveUser?.role === 'superadmin') && (
            <div className="mt-6 pt-6 border-t border-border space-y-1">
              <div className="mb-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start gap-3 h-10 px-3 font-normal text-muted-foreground hover:text-foreground hover:bg-accent/10"
                  onClick={() => {
                    const newExpanded = new Set(expandedItems);
                    if (newExpanded.has('organization')) {
                      newExpanded.delete('organization');
                    } else {
                      newExpanded.add('organization');
                    }
                    setExpandedItems(newExpanded);
                  }}
                >
                  <Building2 className="h-4 w-4" />
                  <span className="text-sm">Organization</span>
                  <svg
                    className={cn(
                      "h-4 w-4 ml-auto transition-transform",
                      expandedItems.has('organization') && "rotate-180"
                    )}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </Button>
                
                {expandedItems.has('organization') && (
                  <div className="ml-4 space-y-1 border-l border-border pl-4">
                    {organizationItems.map((item) => (
                      <Button
                        key={item.id}
                        variant={item.current ? "outline" : "ghost"}
                        size="sm"
                        className={cn(
                          "w-full justify-start gap-3 h-10 px-3 font-normal",
                          item.current ? "bg-accent/10 text-accent border-accent/20" : "text-muted-foreground hover:text-foreground hover:bg-accent/10"
                        )}
                        onClick={() => navigate(item.href)}
                      >
                        <item.icon className="h-4 w-4" />
                        <span className="text-sm">{item.title}</span>
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Admin Section - Only for Super Admin */}
          {(effectiveUser?.role === 'superadmin' || effectiveUser?.isSuperAdmin) && (
            <div className="mt-6 pt-6 border-t border-border space-y-1">
              {adminItems.map((item) => (
                <Button
                  key={item.id}
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start gap-3 h-10 px-3 font-normal text-muted-foreground hover:text-foreground hover:bg-accent/10"
                  onClick={() => {
                    // This will be handled by parent component
                    const event = new CustomEvent('sidebar-action', {
                      detail: { action: item.action }
                    });
                    window.dispatchEvent(event);
                  }}
                >
                  <item.icon className="h-4 w-4" />
                  <span className="text-sm">{item.title}</span>
                </Button>
              ))}
            </div>
          )}

          {/* Feedback Section */}
          <div className="mt-6 pt-6 border-t border-border">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-3 h-10 px-3 font-normal text-muted-foreground hover:text-foreground hover:bg-accent/10"
              onClick={() => setIsFeedbackModalOpen(true)}
            >
              <MessageSquare className="h-4 w-4" />
              <span className="text-sm">Feedback</span>
            </Button>
          </div>
        </div>
      </ScrollArea>

      {/* Logout Section - Fixed at bottom */}
      <div className="px-6 py-4 border-t border-border">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-3 h-10 px-3 font-normal text-muted-foreground hover:text-foreground hover:bg-accent/10"
          onClick={async () => {
            await logout('https://estimation.pk');
          }}
        >
          <LogOut className="h-4 w-4" />
          <span className="text-sm">Logout</span>
        </Button>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
      />
    </aside>
  );
}

export { SidebarLoading };
